<template>
	<ul class="flex flex-col gap-3">
		<li v-for="line in lines" :key="line.date" class="flex items-center gap-3" data-cy="extra_night_pricing_line">
			<span
				class="flex size-9 flex-none flex-col items-center justify-center gap-1 rounded-chip border border-line bg-surface leading-trim"
			>
				<span class="text-micro font-semibold text-ink">{{ line.weekday }}</span>
				<span class="text-micro font-medium text-muted">{{ line.dayOfMonth }}</span>
			</span>

			<div class="flex min-w-0 flex-1 flex-col gap-1">
				<span class="flex flex-wrap items-center gap-1">
					<span class="text-sm font-medium text-ink">{{ line.label }}</span>
					<span
						v-if="line.isArrival"
						class="inline-flex items-center rounded bg-primary px-1.5 py-1 text-micro font-medium leading-none text-primary-letter whitespace-nowrap"
					>
						{{ t('arrangement_badge') }}
					</span>
				</span>
				<span class="text-xs text-muted">{{ line.subtitle }}</span>
			</div>

			<PriceText v-if="line.price" :value="line.price" class="flex-none text-base text-ink" />
		</li>
	</ul>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { addDays, format, parse } from 'date-fns';
import { dateLocale as locale } from '@/config';
import { useExtraNightPricing } from '@/composables/useExtraNightPricing';
import PriceText from '@/components/PriceText.vue';

interface DisplayLine {
  date: string
  weekday: string
  dayOfMonth: string
  label: string
  subtitle: string
  price: string | null
  isArrival: boolean
}

const { t } = useI18n();
const { lines: pricedNights } = useExtraNightPricing();

// The API sends only the billed nights; the unbilled departure day is appended for display.
const lines = computed<DisplayLine[]>(() => {
  const priced = pricedNights.value;

  const nights = priced.map((line, index) => ({
    date: line.date,
    label: index === 0 ? t('arrival') : t('nth_night', { n: index + 1 }),
    subtitle: index === 0 ? t('arrival_night_subtitle') : t('extra_night_subtitle'),
    price: line.price_formatted,
    isArrival: index === 0,
    ...dayBadge(line.date),
  }));

  if (nights.length === 0) return [];

  const departureDate = format(addDays(parseDate(priced[priced.length - 1].date), 1), 'yyyy-MM-dd');

  return [
    ...nights,
    {
      date: departureDate,
      label: t('departure'),
      subtitle: t('departure_day_subtitle'),
      price: null,
      isArrival: false,
      ...dayBadge(departureDate),
    },
  ];
});

function parseDate(date: string): Date {
  return parse(date, 'yyyy-MM-dd', new Date());
}

function dayBadge(date: string): { weekday: string, dayOfMonth: string } {
  return {
    weekday: format(parseDate(date), 'EEEEEE', { locale }),
    dayOfMonth: format(parseDate(date), 'd', { locale }),
  };
}
</script>
